import React, { useState, FormEvent, ChangeEvent, JSX } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    User,
    Mail,
    Phone,
    Lock,
    Loader2,
    AlertCircle,
    ArrowRight,
    BadgeCheck,
    HardHat // Fitting for a construction group
} from "lucide-react";

interface RegisterForm {
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
    password?: string;
}

export default function Register(): JSX.Element {
    const [form, setForm] = useState<RegisterForm>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const nav = useNavigate();

    // Corporate Brand Colors
    const brandBlue = "#0054A6"; 
    const brandYellow = "#FFD100";

    const handleRegister = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        
        // Final validation check
        if (form.email && !form.email.includes("@ikkgroup.com")) {
            setError("Invalid corporate domain.");
            return;
        }

        setIsLoading(true);
        setError("");
        
        // Mocking API delay
        setTimeout(() => {
            setIsLoading(false);
            nav("/verify");
        }, 1500);
    };

    const fields = [
        { id: "name", label: "Full Name", type: "text", icon: BadgeCheck, placeholder: "John Doe" },
        { id: "username", label: "Username", type: "text", icon: User, placeholder: "johndoe123" },
        { id: "email", label: "Corporate Email", type: "text", icon: Mail, placeholder: "username", isCorporate: true },
        { id: "phone", label: "Phone Number", type: "tel", icon: Phone, placeholder: "+966 ..." },
        { id: "password", label: "Password", type: "password", icon: Lock, placeholder: "••••••••" },
    ];

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0f1d] relative overflow-hidden py-12 font-sans">
            {/* Structural Background Accents */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                <div className={`absolute top-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full blur-[120px]`} style={{ backgroundColor: `${brandBlue}33` }} />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-[540px] px-6"
            >
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-8">
                    {/* Header with Logo */}
                    <header className="flex flex-col items-center mb-10">
                        <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                            <img
                                src="/logo.jpeg" // Replace with your actual path
                                alt="Kabbani Construction Group"
                                className="h-16 object-contain"
                            />
                        </div>
                        <h1 className="text-xl font-black text-white uppercase tracking-wider text-center mb-4">
                            Attendance Management System
                        </h1>
                        <h1 className="text-2xl font-black text-white uppercase tracking-wider">
                            Employee Portal
                        </h1>
                        <div className="h-1 w-12 mt-2" style={{ backgroundColor: brandYellow }} />
                    </header>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="mb-6 p-4 rounded-lg bg-red-500/10 border-l-4 border-red-500 text-red-200 text-sm flex items-center gap-3"
                            >
                                <AlertCircle size={18} />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleRegister} className="space-y-5">
                        {fields.map((field) => (
                            <div key={field.id} className="group">
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1 tracking-widest">
                                    {field.label}
                                </label>
                                <div className="relative flex items-center">
                                    <div className="absolute left-4 text-slate-500 group-focus-within:text-[#FFD100] transition-colors">
                                        <field.icon size={18} />
                                    </div>
                                    
                                    <input
                                        required
                                        type={field.type}
                                        placeholder={field.placeholder}
                                        onChange={(e) => {
                                            const val = field.isCorporate ? `${e.target.value}@ikkgroup.com` : e.target.value;
                                            setForm({ ...form, [field.id]: val });
                                        }}
                                        className={`w-full bg-slate-900/50 border border-slate-700 text-white pl-12 ${field.isCorporate ? 'pr-36' : 'pr-4'} py-3.5 rounded-xl focus:border-[#FFD100] outline-none transition-all placeholder:text-slate-600 text-sm shadow-inner`}
                                    />

                                    {field.isCorporate && (
                                        <div className="absolute right-3 bg-slate-800 px-2 py-1 rounded border border-slate-600 text-[11px] font-bold text-slate-400">
                                            @ikkgroup.com
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        <button
                            disabled={isLoading}
                            type="submit"
                            style={{ backgroundColor: brandBlue }}
                            className="w-full mt-6 text-white font-bold py-4 rounded-xl shadow-lg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3 border-b-4 border-black/20"
                        >
                            {isLoading ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                <>
                                    REGISTER TO GROUP
                                    <HardHat size={18} className="text-[#FFD100]" />
                                </>
                            )}
                        </button>
                    </form>

                    <footer className="mt-8 text-center pt-6 border-t border-white/5">
                        <p className="text-slate-400 text-sm">
                            Already a member?{" "}
                            <Link
                                to="/"
                                className="font-bold hover:underline transition-colors"
                                style={{ color: brandYellow }}
                            >
                                Sign In here
                            </Link>
                        </p>
                    </footer>
                </div>
            </motion.div>
        </div>
    );
}